import { createSlice } from "@reduxjs/toolkit";

// The user's location — a chosen address or a geocoded GPS fix (see `source`).
// The backend does persist it (UpdateClientLocation → the Login response), but
// Login lands mid-handshake, seconds after first paint; localStorage is the cache
// that lets the home pill and the carousel coordinates survive a reload or a new
// deep-link tab without waiting for it. localStorage, not sessionStorage: partner
// deep links open new tabs, and staleness self-heals because home re-resolves GPS
// in the background on mount when permission is granted (useEntryGpsRefresh).
const hydrateClientAddress = () => {
  // One-time cleanup: the GPS fix used to live in its own sessionStorage key;
  // it is now part of clientAddress (source: "gps").
  try {
    sessionStorage.removeItem("userLocation");
  } catch {
    // Storage unavailable — nothing to clean.
  }

  try {
    const stored = JSON.parse(localStorage.getItem("clientAddress"));
    if (!stored) return null;
    // Values written before `source` existed are always hand-picked or geocoded
    // addresses, never bare fixes.
    return { source: "address", ...stored };
  } catch {
    return null;
  }
};

export const slice = createSlice({
  name: "userSlice",
  initialState: {
    mainUserData: {},
    userData: {},
    showHealthForm: false,
    showTermOfUsePage: false,
    isHaveCreditCard: false,
    userToken: "",
    clientAddress: hydrateClientAddress(),
  },
  reducers: {
    updateMainUserData: (state, action) => {
      state.mainUserData = action.payload;
    },
    updateUserData: (state, action) => {
      state.userData = action.payload;
    },
    updateDisplayingHealthForm: (state, action) => {
      state.showHealthForm = action.payload;
    },
    updateDisplayingTermOfUsePage: (state, action) => {
      state.showTermOfUsePage = action.payload;
    },
    updateUserToken: (state, action) => {
      state.userToken = action.payload;
    },
    updateIsHaveCreditCard: (state, action) => {
      state.isHaveCreditCard = action.payload;
    },
    // Persists from inside the reducer (the setPartner/savePartnerKey precedent
    // in partnerStore) because several flows dispatch this — the address screen,
    // welcome, the location gate and the deep-link paths in LayoutV2 — and a
    // caller that forgets the write would silently lose the location on the next
    // refresh.
    updateClientAddress: (state, action) => {
      state.clientAddress = action.payload;
      try {
        localStorage.setItem("clientAddress", JSON.stringify(action.payload));
      } catch {
        // Storage unavailable — the location just resolves again next entry.
      }
    },
  },
});

export const {
  updateUserData,
  updateMainUserData,
  updateDisplayingHealthForm,
  updateDisplayingTermOfUsePage,
  updateUserToken,
  updateIsHaveCreditCard,
  updateClientAddress,
} = slice.actions;

export const selectClientAddress = (state) => state.userSlice.clientAddress;

export default slice.reducer;
